import { FitsSchema } from "functions/networking/JsonSchema";
import { DeviceInfo, FetchOptions, FetchResult, Fetchable, IsIOState, IsOnlineData, IsShortOnlineData, MsgModes, RType, SetOptions, SetResult, energy, energyKeys, SupportedDevices, TYPE_PLA, TYPE_GCR, gcr12_meas_schema, gcr12_stage_schema, DataType, Message, ParsableResponses, IsResponseHead, ParseDayFiles, FetchMessage, ParseHarm, TYPE_GCRT, gcr12_alarms_schema, TYPE_ICR, icr12_meas_schema } from "functions/networking/NetTypes";
import { FetchFake }  from "functions/networking/Notworking";
import { MqttClient } from "mqtt/dist/mqtt";
import config from "config.json";


const FAKE = false;

const MSG_TIMEOUT = 5e3;
const requestTopic = "application_request";
export const responseTopic = "application_data";

export const dvc_root = {
	[TYPE_PLA] : "pla",
	[TYPE_GCR] : "GCR12",
	[TYPE_GCRT] : "GCR12",
	[TYPE_ICR] : "ICR12",
};



export type FetchListener<D extends SupportedDevices, T extends Fetchable<D>> = { topic: string, request: T, handler: (val: RType<T, D>) => void }

function Remove<T>(arr : T[], elem : T, hint? : number) {
	let i : number;
	if (hint && arr[hint] === elem) {
		i = hint;
	} else {
		i = arr.indexOf(elem);
		if (i < 0) return false;
	}
	if (arr.length === i + 1) arr.pop();
	else arr[i] = arr.pop()!;
	return true;
}

function StartsWith<T extends string>(s : string, t : T) : s is `${T}${any}` {
	return s.startsWith(t);
}

function CheckResponse<D extends SupportedDevices, T extends Fetchable<D>>(
	device: D, request: T, data: any
): data is DataType<D, T> {
	if (StartsWith(request, "Online")) {
		if (request === "Online_Short") {
			return IsShortOnlineData(data, device);
		} else {
			if (request !== "Online") {
				const nn3 = [NaN, NaN, NaN]
				data["Rotation"] = nn3;
				if (request === "Online_Min") {
					data["Over_Deviation_LL"] = nn3;
					data["Over_Deviation_LN"] = nn3;
				} else if (request === "Online_Max") {
					data["Under_Deviation_LL"] = nn3;
					data["Under_Deviation_LN"] = nn3;
				}
				const nenergy : energy = {
					Consumption: {
						Active_kWh: NaN,
						Capacity_kvarh: NaN,
						Inductive_kvarh: NaN
					},
					Distribution: {
						Active_kWh: NaN,
						Capacity_kvarh: NaN,
						Inductive_kvarh: NaN
					}
				}
				energyKeys.forEach(k => data[k] = nenergy);
			}

			return IsOnlineData(data);
		}
	}
	else if (StartsWith(request, "Harm")) {
		if (typeof data !== "object") return false;
		const nrPhases =
			device === TYPE_GCR ? 1 :
			device === TYPE_GCRT ? 4 :
			3;
		for (let i = 1; i <= 40; i++) {
			const id = i.toString().padStart(2, '0');
			const val = data[`H_${id}`];

			if (val === undefined) return true;
			if (!Array.isArray(val)
				|| val.length !== nrPhases
				|| val.some(v => typeof v !== "number" && v !== null)
			) {
				return false;
			}
		}
		return true;
	}
	else if (request === "Energy_Per_Day_Folder_Info" || request === "Energy_Folder_Info") {
		return true; // TODO: ?
	}
	else if (request === "IO_State") {
		return IsIOState(data);
	}
	else if (request === "Meas") {
		return FitsSchema(data, gcr12_meas_schema) || FitsSchema(data, icr12_meas_schema);
	}
	else if (request === "Stages") {
		return FitsSchema(data, gcr12_stage_schema);
	}
	else if (request === "Alarms") {
		return FitsSchema(data, gcr12_alarms_schema);
	}
	else {
		let _ : `Energy_${any}` = request;
		console.warn("Unhandled response", _, data);
		return false;
	}
}


function MessageEq(a: Message<any>, b: Message<any>) : boolean {
	if (a === b) return true;

	if (a.mode !== MsgModes.Get) return false;
	if (b.mode !== MsgModes.Get) return false;
	if (a.request !== b.request) return false;
	if (a.topic !== b.topic)     return false;
	if (a.options !== b.options) return false; // sice options muze byt objekt, ale v tomhle pripade to nevadi

	return true;
}

const SequentialFields //: readonly (keyof ShortOnlineData)[]
= {
	[TYPE_PLA]:  ["U_LN", "U_LL", "Current", "P", "Q", "S", "Frequency", "CosFi_Mod"] satisfies (keyof DataType<typeof TYPE_PLA, "Online_Short">)[],
	[TYPE_GCR]:  ["U_LL", "Current", "P", "Q", "S", "Frequency", "CosFi_Mod"]         satisfies (keyof DataType<typeof TYPE_GCR, "Online_Short">)[],
	[TYPE_GCRT]: ["U_LL", "Current", "P", "Q", "S", "Frequency", "CosFi_Mod"]         satisfies (keyof DataType<typeof TYPE_GCRT, "Online_Short">)[],
	[TYPE_ICR]:  ["P", "Q", "S", "CosFi_Mod"]                                         satisfies (keyof DataType<typeof TYPE_ICR, "Online_Short">)[],
} as const;
export class DeviceConnection<D extends SupportedDevices> {
	public readonly user: string;
	public readonly client: MqttClient;
	public readonly device: DeviceInfo<D>;
	public readonly dvcKind: D;
	sendQueue   : Message<any>[] = [];
	recieveQueue: Message<any>[] = [];
	dead = false;

	readonly listener;
	listeners : FetchListener<SupportedDevices, Fetchable<SupportedDevices>>[] = [];

	private readonly topic_request;
	private readonly topic_response;

	// TODO: move onlineData to universal cache
	seqData: {
		onlineData: RType<"Online_Short", D> | null,
		seqData: { [k in typeof SequentialFields[D][number]] : RType<"Online_Short", D>["Data"][any][] };
		times: Date[]
		generation : number,
	};

	constructor(user: string, client: MqttClient, device: DeviceInfo<D>) {
		this.user   = user;
		this.client = client;
		this.device = device;
		this.dvcKind = device._type as D;

		this.listener = this.Recieve.bind(this);
		this.client.on("message", this.listener);
		this.topic_response = `${user}/${dvc_root[device._type]}/${device.Mqtt_uid}/${responseTopic}`;
		this.topic_request  = `${user}/${dvc_root[device._type]}/${device.Mqtt_uid}/${requestTopic}`;
		this.client.subscribe(this.topic_response);


		let seqData = {  } as any;
		for (let i = 0; i < SequentialFields[device._type].length; i++) {
			seqData[SequentialFields[device._type][i]] = [];
		}

		this.seqData = {
			onlineData: null,
			seqData: seqData,
			times: [],
			generation: 0,
		};
	}

	public Stop() {
		this.dead = true;

		this.client.removeListener("message", this.listener);
		if (this.client.connected && !this.client.disconnecting) {
			this.client.unsubscribe(this.topic_response);
		}

		for (const msg of this.sendQueue)    msg.res({ err: true, kind: "canceled" });
		for (const msg of this.recieveQueue) msg.res({ err: true, kind: "canceled" });

		this.sendQueue    = [];
		this.recieveQueue = [];
	}

	public IsFrom<A extends D>(type: A | A[]): this is DeviceConnection<A> {
		if (Array.isArray(type)) {
			return type.some(d => this.device._type === d);
		}
		else return this.device._type === type;
	}

	senderRunning = false;
	private async SendMessagesInQueue() {
		if (this.senderRunning) return;
		try {
			this.senderRunning = true;

			while (this.sendQueue.length) {
				let msg = this.sendQueue[0];

				let toDrop = 0;
				let resolvers : typeof msg.res[] = [];
				for (let i = 0; i + toDrop < this.sendQueue.length; i++) {
					this.sendQueue[i] = this.sendQueue[i + toDrop];

					let qmsg = this.sendQueue[i];
					if (!MessageEq(qmsg, msg)) continue;

					resolvers.push(qmsg.res);
					toDrop++;
					i--;
				}
				this.sendQueue = this.sendQueue.slice(0, this.sendQueue.length - toDrop);

				if (resolvers.length === 0) continue;
				else if (resolvers.length > 1) {
					const ResolveAll : typeof msg.res = (val: FetchResult<any, any>) => {
						for (let i = 0; i < resolvers.length; i++) {
							resolvers[i](val);
						}
					}
					msg.res = ResolveAll;
				}

				const timedOut = { current: false };
				setTimeout(() => {
					if (Remove(this.recieveQueue, msg)) {
						console.warn("timed out", msg.request, msg.options);
						msg.res({ err: true, kind: "timeout" });
					}
					timedOut.current = true;
				}, MSG_TIMEOUT);

				let sent = await new Promise(res => {
					if (this.client === null || this.dead) {
						res(false);
						return;
					}

					let req = `{"Command": "${msg.mode === MsgModes.Get ? "Get" : "Set"}_${msg.request}"${
						msg.options === null ? "" :
							","+Object.keys(msg.options).map(k => `"${k}": ${msg.options![k as keyof typeof msg.options]}`).join(",")
					}}`;

					if (config.DEBUG) {
						try   { console.debug("SEND", JSON.parse(req)); }
						catch { console.warn(req); }
					}

					this.client.publish(
						msg.topic,
						req,
						{},
						e => {
							if (e !== undefined) {
								console.error(e);
								res(false);
							} else {
								res(true);
							}
						});
				}); // sent = await ...

				if (this.dead) break; // while

				if (!sent) {
					msg.res({ err: true, kind: "send_fail" });
					continue;
				}

				if (msg.mode === MsgModes.Set) {
					msg.res({ err: false });
					continue;
				}

				msg.topic = msg.topic.replace(/request$/, "data");
				this.recieveQueue.push(msg);

				if (this.recieveQueue.length > 2) {
					await new Promise(res => setTimeout(res, 100));
				}
			}
		} finally {
			this.senderRunning = false;
		}
	}

	private Send(msg: Message<any>) {
		this.sendQueue.push(msg);
		if (!this.senderRunning)
			this.SendMessagesInQueue();
	}

	private DistributeResult<D extends SupportedDevices, T extends ParsableResponses<D>, U extends Fetchable<D> & T>(topic : string, request : T, data : FetchResult<D, U>) {
		let i = 0;
		while (i < this.recieveQueue.length) {
			let msg = this.recieveQueue[i];
			if (msg.topic === topic && request === msg.request) {
				msg.res(data);
				Remove(this.recieveQueue, msg, i);
			}
			else i++;
		}
		if (!data.err)
			for (let l of this.listeners) {
				if (l.topic === topic && l.request === request) {
					l.handler(data.val);
				}
			}
	}

	private Recieve(topic : string, payload : Buffer) {
		try {
			let o = JSON.parse(payload.toString());
			if (typeof o !== "object") {
				console.warn("failed to parse response", payload.toString());
				return;
			}
			if (config.DEBUG) console.debug("RECV", o?.Head?.File_type, o);
			let head = o.Head;
			if (!IsResponseHead(head)) return;

			let data = o.Data;
			if (head.File_type === "Energy_Per_Day_File" || head.File_type === "Energy_File") {
				let res = ParseDayFiles(data);
				if (!res.success) {
					console.error("Failed to parse day file", data);
					return;
				}

				for (let i = 0; i < this.recieveQueue.length; i++) {
					let msg = this.recieveQueue[i];
					if ((msg.request !== head.File_type && msg.request !== "Energy_File")
						|| msg.topic !== topic
						|| res.value[(msg as FetchMessage<typeof TYPE_PLA, "Energy_Per_Day_File"> | FetchMessage<typeof TYPE_PLA, "Energy_File">).options.File_ID] === undefined
					) continue;

					msg.res({ err: false, val: { Head: head, Data: res.value }});
					Remove(this.recieveQueue, msg);
					i--;
				}
			}
			else if (!CheckResponse(head._type, head.File_type, data)) {
				if (config.DEBUG) { console.warn("Could not parse response", head.File_type, data); }
				this.DistributeResult(topic, head.File_type, { err: true, kind: "parse_fail" });
			}
			else if (StartsWith(head.File_type, "Harm")) {
				this.DistributeResult(topic, head.File_type, { err: false, val: { Head: head, Data: ParseHarm(data, head) } });
			}
			else {
				this.DistributeResult(topic, head.File_type, { err: false, val: { Head: head, Data: data as any } });
			}
		} catch (e) {
			console.log(payload.toString());
			console.error(e);
		}
	}

	public AddFetchListener(l : FetchListener<any, any>) {
		if (!this.listeners.includes(l)) this.listeners.push(l);
	}

	public RemoveFetchListener(l : FetchListener<any, any>) {
		Remove(this.listeners, l);
	}

	public async Fetch<D extends SupportedDevices, T extends Fetchable<D>>(what : T, options : FetchOptions<D, T>) : Promise<FetchResult<D, T>> {
		if (FAKE) {
			return new Promise(res => setTimeout(() => res(
				{ err: false, val : FetchFake(what) as RType<T, D> }
			), 100));
		}

		return new Promise(res => this.Send({
			topic: this.topic_request,
			request: what,
			res: res,
			options,
			mode: MsgModes.Get
		}));
	}

	public async SetUnitConfig<K extends keyof SetOptions>(what : K, options : SetOptions[K]) : Promise<SetResult> {
		if (FAKE) {
			return new Promise(res => setTimeout(() => res({ err: false }), 100));
		}

		return new Promise(res => this.Send({
			topic: this.topic_request,
			request: what,
			res,
			options,
			mode: MsgModes.Set
		}));
	}
}



